/**
 * The key a write carries.
 *
 * Every POST, PATCH and DELETE of the Map Studio API declares `Idempotency-Key` but
 * `postSession`, `postOrganizationLookup` and the two collaboration signals, `postItineraryCollab`
 * and `postZoneCollab`, which a repeat restates; the Tile API has no write. The API answers a key
 * it has seen with the answer it stored rather than run the write again. That is what makes
 * asking again safe after an answer that never came, a 502, a 503 or a 504: a call sends one key
 * on every attempt, its caller's or one drawn here before the first attempt and never again for
 * that call, and an operation that declares no key sends none.
 *
 * The key is drawn from a cryptographic random source or not at all. Two calls that drew the same
 * key would each be answered with the other's stored answer, which is a defect no retry policy
 * can recover from, so a runtime with no such source sends no key, and its writes are then
 * repeated only where the operation declares itself idempotent.
 */

/** The cryptographic source a key is drawn from, read off the runtime at each draw. */
interface RandomSource {
  readonly getRandomValues?: (<T extends Uint8Array>(array: T) => T) | undefined;
  readonly randomUUID?: (() => string) | undefined;
}

/** The runtime's `crypto`, read at each draw rather than once, so a test can take it away. */
const randomSource = (): RandomSource | undefined => {
  const source: unknown = Reflect.get(globalThis, "crypto");

  return typeof source === "object" && source !== null ? source : undefined;
};

/** Spells sixteen bytes as a version 4 UUID, in its lowercase canonical form. */
const uuidOf = (bytes: Uint8Array): string => {
  // The version, 4, in the high nibble of the seventh byte, and the RFC 9562 variant, 10, in the
  // two high bits of the ninth.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * mintIdempotencyKey draws the key one call of a write sends on every attempt: a version 4 UUID
 * from the runtime's `crypto.randomUUID`, or built from `crypto.getRandomValues` where a runtime
 * has the one and not the other, or none at all where it has neither.
 *
 * Never from `Math.random`: a weaker source makes two calls drawing the same key a matter of
 * time, and the API answers the second with the first one's answer.
 *
 * @returns The key, or undefined where the runtime offers no cryptographic random source.
 */
export const mintIdempotencyKey = (): string | undefined => {
  const source = randomSource();

  if (typeof source?.randomUUID === "function") {
    return source.randomUUID().toLowerCase();
  }

  if (typeof source?.getRandomValues === "function") {
    return uuidOf(source.getRandomValues(new Uint8Array(16)));
  }

  return undefined;
};
